/**
 * Pagina Impostazioni Azienda — profilo aziendale completo FatturaPA.
 *
 * Tab: Dati Aziendali, Sede Legale, REA e Fiscale, Contatti, Sedi Operative.
 * Visibile solo ad admin e super_admin.
 * Endpoint: GET/PATCH /api/v1/admin/azienda
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ModalitaPagamentoEditor } from './ModalitaPagamentoEditor';
import { ModelliEmailEditor } from './ModelliEmailEditor';
import { UnitaMisuraEditor } from './UnitaMisuraEditor';

/* ------------------------------------------------------------------ */
/* Tipi                                                               */
/* ------------------------------------------------------------------ */

interface AziendaData {
  id: string;
  nome: string;
  tipo_soggetto: string | null;
  forma_giuridica: string | null;
  denominazione: string | null;
  nome_legale: string | null;
  cognome_legale: string | null;
  titolo: string | null;
  codice_fiscale: string | null;
  partita_iva: string | null;
  id_paese: string | null;
  cod_eori: string | null;
  regime_fiscale: string | null;
  albo_professionale: string | null;
  provincia_albo: string | null;
  numero_iscrizione_albo: string | null;
  data_iscrizione_albo: string | null;
  sede_indirizzo: string | null;
  sede_civico: string | null;
  sede_cap: string | null;
  sede_comune: string | null;
  sede_provincia: string | null;
  sede_nazione: string | null;
  stabile_org_indirizzo: string | null;
  stabile_org_civico: string | null;
  stabile_org_cap: string | null;
  stabile_org_comune: string | null;
  stabile_org_provincia: string | null;
  stabile_org_nazione: string | null;
  rea_ufficio: string | null;
  rea_numero: string | null;
  rea_capitale_sociale: number | null;
  rea_socio_unico: string | null;
  rea_stato_liquidazione: string | null;
  telefono: string | null;
  fax: string | null;
  email: string | null;
  pec: string | null;
  codice_sdi: string | null;
  riferimento_amministrazione: string | null;
  logo_path: string | null;
  campi_mancanti?: string[];
}

interface SedeData {
  id: string;
  nome: string;
  tipo: string;
  codice: string | null;
  indirizzo: string | null;
  civico: string | null;
  cap: string | null;
  citta: string | null;
  provincia: string | null;
  nazione: string | null;
  telefono: string | null;
  email: string | null;
  pec: string | null;
  iban: string | null;
  banca: string | null;
  intestatario_conto: string | null;
  regime_fiscale: string | null;
  logo_path: string | null;
  stato: string;
  data_chiusura: string | null;
}

/* ------------------------------------------------------------------ */
/* Costanti                                                           */
/* ------------------------------------------------------------------ */

const TIPI_SOGGETTO = [
  { value: 'societa_capitali', label: 'Società di capitali (SRL, SPA, SAPA)' },
  { value: 'societa_persone', label: 'Società di persone (SNC, SAS)' },
  { value: 'ditta_individuale', label: 'Ditta individuale' },
  { value: 'professionista', label: 'Professionista' },
  { value: 'associazione', label: 'Associazione' },
  { value: 'cooperativa', label: 'Cooperativa' },
  { value: 'ente_pubblico', label: 'Ente pubblico' },
  { value: 'altro', label: 'Altro' },
];

const REGIMI_FISCALI = [
  { value: 'RF01', label: 'RF01 — Ordinario' },
  { value: 'RF02', label: 'RF02 — Contribuenti minimi' },
  { value: 'RF04', label: 'RF04 — Agricoltura e pesca' },
  { value: 'RF05', label: 'RF05 — Vendita sali e tabacchi' },
  { value: 'RF06', label: 'RF06 — Commercio fiammiferi' },
  { value: 'RF07', label: 'RF07 — Editoria' },
  { value: 'RF08', label: 'RF08 — Telefonia pubblica' },
  { value: 'RF09', label: 'RF09 — Trasporto pubblico e sosta' },
  { value: 'RF10', label: 'RF10 — Intrattenimenti e giochi' },
  { value: 'RF11', label: 'RF11 — Agenzie viaggi e turismo' },
  { value: 'RF12', label: 'RF12 — Agriturismo' },
  { value: 'RF13', label: 'RF13 — Vendite a domicilio' },
  { value: 'RF14', label: 'RF14 — Beni usati, arte, antiquariato' },
  { value: 'RF15', label: 'RF15 — Agenzie vendite all\'asta' },
  { value: 'RF16', label: 'RF16 — IVA per cassa P.A.' },
  { value: 'RF17', label: 'RF17 — IVA per cassa' },
  { value: 'RF18', label: 'RF18 — Altro' },
  { value: 'RF19', label: 'RF19 — Regime forfettario' },
];

/* ------------------------------------------------------------------ */
/* Componente helper: campo input con label                           */
/* ------------------------------------------------------------------ */

const Campo: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  required?: boolean;
}> = ({ label, value, onChange, placeholder, maxLength, disabled, required }) => (
  <div className="space-y-1">
    <Label className="text-sm font-medium">
      {label}
      {required && <span style={{ color: 'var(--color-destructive)' }}> *</span>}
    </Label>
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      disabled={disabled}
    />
  </div>
);

/* ------------------------------------------------------------------ */
/* LogoUploader — upload + rimozione logo per tenant o ramo           */
/* ------------------------------------------------------------------ */

interface LogoUploaderProps {
  token: string;
  uploadUrl: string;     // POST multipart/form-data
  deleteUrl?: string;    // DELETE (opzionale)
  /** URL GET autenticato per scaricare il file logo (es. /api/v1/admin/azienda/logo/file). Se omesso, deriva da uploadUrl + /file */
  previewUrl?: string;
  logoPath: string | null;
  hint?: string;
  onChange: (newPath: string | null) => void;
}

const LogoUploader: React.FC<LogoUploaderProps> = ({
  token, uploadUrl, deleteUrl, previewUrl, logoPath, hint, onChange,
}) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Fetch logo come blob (auth header) e crea ObjectURL per anteprima
  useEffect(() => {
    if (!logoPath) {
      setPreviewSrc(null);
      return;
    }
    const url = previewUrl || `${uploadUrl}/file`;
    let revoked = false;
    let createdUrl: string | null = null;
    setLoadingPreview(true);
    setErr(null);

    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Anteprima non disponibile (${res.status})`);
        const blob = await res.blob();
        if (revoked) return;
        createdUrl = URL.createObjectURL(blob);
        setPreviewSrc(createdUrl);
      })
      .catch((e) => {
        if (!revoked) setErr(e instanceof Error ? e.message : 'Errore anteprima');
      })
      .finally(() => {
        if (!revoked) setLoadingPreview(false);
      });

    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [logoPath, previewUrl, uploadUrl, token]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/svg+xml'].includes(file.type)) {
      setErr('Formato non supportato (PNG/JPG/SVG)');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setErr('File troppo grande (max 2MB)');
      return;
    }
    setUploading(true);
    setErr(null);

    // Anteprima locale immediata (prima che l'upload finisca)
    const localUrl = URL.createObjectURL(file);
    setPreviewSrc(localUrl);

    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Errore ${res.status}`);
      }
      const j = await res.json();
      onChange(j.logo_path || null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore upload');
      // ripristina src precedente al fallimento (verra' re-fetched dall'useEffect)
      URL.revokeObjectURL(localUrl);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!deleteUrl) return;
    setUploading(true);
    setErr(null);
    try {
      const res = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      onChange(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore rimozione');
    } finally {
      setUploading(false);
    }
  };

  const filename = logoPath ? logoPath.split('/').pop() : null;
  const isSvg = filename?.toLowerCase().endsWith('.svg');

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Logo</Label>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml"
        onChange={handleFile}
        style={{ display: 'none' }}
      />

      <div className="flex items-start gap-4 flex-wrap">
        {/* Box anteprima - simula l'intestazione del documento PDF.
            Dimensioni 320x128px = scala 2x di 160x64 (max nel template PDF).
            object-fit: contain garantisce che qualunque dimensione del file
            originale (quadrato, banner, alto) venga adattata alle proporzioni
            dell'intestazione documento senza deformare. */}
        <div className="space-y-1">
          <div
            className="rounded-md border flex items-center justify-start overflow-hidden"
            style={{
              width: 320,
              height: 128,
              background:
                'repeating-conic-gradient(var(--color-bg-subtle, #F4F6FB) 0% 25%, transparent 0% 50%) 50% / 16px 16px, var(--color-background, white)',
              borderColor: 'var(--color-border, #e5e7eb)',
              borderStyle: previewSrc ? 'solid' : 'dashed',
              padding: 16,
            }}
          >
            {loadingPreview ? (
              <span className="text-xs w-full text-center" style={{ color: 'var(--color-muted-foreground)' }}>
                Caricamento anteprima...
              </span>
            ) : previewSrc ? (
              <img
                src={previewSrc}
                alt="Logo"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  objectPosition: 'left center',
                }}
              />
            ) : (
              <div className="w-full text-center">
                <div className="text-xs font-medium" style={{ color: 'var(--color-muted-foreground)' }}>
                  Nessun logo
                </div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>
                  PNG / JPG / SVG
                </div>
              </div>
            )}
          </div>
          <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
            Anteprima intestazione documento (allineamento sinistro, max 160x64 nel PDF).
            Qualunque dimensione del file viene adattata mantenendo le proporzioni.
          </p>
        </div>

        {/* Controlli */}
        <div className="flex flex-col gap-2 min-w-[180px]">
          <Button
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            {uploading
              ? 'Caricamento...'
              : (logoPath ? 'Sostituisci logo' : 'Carica logo')}
          </Button>
          {logoPath && deleteUrl && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              disabled={uploading}
              style={{ color: 'var(--color-destructive)' }}
            >
              Rimuovi
            </Button>
          )}
          {filename && (
            <div className="text-xs space-y-0.5">
              <div className="font-mono truncate" title={filename} style={{ maxWidth: 200 }}>
                {filename}
              </div>
              {isSvg && (
                <div style={{ color: 'var(--color-muted-foreground)' }}>SVG (vettoriale)</div>
              )}
            </div>
          )}
        </div>
      </div>

      {hint && (
        <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
          {hint}
        </p>
      )}
      {err && (
        <p className="text-xs" style={{ color: 'var(--color-destructive)' }}>
          {err}
        </p>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Componente principale                                              */
/* ------------------------------------------------------------------ */

/** Valori validi del parametro ?tab= (uno per TabsTrigger). */
const TAB_VALIDI = [
  'documenti',
  'anagrafica',
  'sede',
  'fiscale',
  'contatti',
  'pagamenti',
  'unita-misura',
  'modelli-email',
  'sedi',
  'multitenant',
] as const;

const ImpostazioniAzienda: React.FC = () => {
  const token = localStorage.getItem('token') ?? '';
  const [data, setData] = useState<AziendaData | null>(null);
  const [sedi, setSedi] = useState<SedeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editData, setEditData] = useState<Record<string, string>>({});
  const [sedeEdit, setSedeEdit] = useState<Record<string, Record<string, string>>>({});

  /* Tab attivo persistito nella URL (?tab=...): il salvataggio non cambia
     scheda e il back del browser ripristina quella precedente. */
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tabAttivo =
    tabParam && (TAB_VALIDI as readonly string[]).includes(tabParam) ? tabParam : 'documenti';
  const cambiaTab = (value: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('tab', value);
      return p;
    });
  };

  /* Scope intestazione documenti: 'tenant' o uo_id */
  const [scopoIntestazione, setScopoIntestazione] = useState<string>('tenant');

  /* Multi-tenant / multi-ramo */
  const [showNewRamo, setShowNewRamo] = useState(false);
  const [newRamoNome, setNewRamoNome] = useState('');
  const [newRamoTipo, setNewRamoTipo] = useState('divisione');
  const [savingRamo, setSavingRamo] = useState(false);
  /* Rinomina ramo */
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  /* Chiusura ramo — modale conferma */
  const [closingRamo, setClosingRamo] = useState<SedeData | null>(null);
  const [closeConfirmName, setCloseConfirmName] = useState('');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  /* ---- Caricamento ---- */

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [azRes, sediRes] = await Promise.all([
        fetch('/api/v1/admin/azienda', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/v1/unita?include_chiuse=true', { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (!azRes.ok) throw new Error(`Errore ${azRes.status}`);
      const az: AziendaData = await azRes.json();
      setData(az);
      // Inizializza editData con i valori correnti
      const ed: Record<string, string> = {};
      for (const [k, v] of Object.entries(az)) {
        if (v !== null && v !== undefined && typeof v !== 'object') ed[k] = String(v);
      }
      setEditData(ed);

      if (sediRes.ok) {
        const s: SedeData[] = await sediRes.json();
        setSedi(s);
        const se: Record<string, Record<string, string>> = {};
        for (const sede of s) {
          se[sede.id] = {};
          for (const [k, v] of Object.entries(sede)) {
            if (v !== null && v !== undefined && typeof v !== 'object') se[sede.id][k] = String(v);
          }
        }
        setSedeEdit(se);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { void carica(); }, [carica]);

  /* Auto-seleziona il primo ramo attivo per il tab Intestazione Documenti */
  useEffect(() => {
    if (scopoIntestazione === 'tenant' && sedi.length > 0) {
      const firstActive = sedi.find((s) => s.stato !== 'chiuso');
      if (firstActive) setScopoIntestazione(firstActive.id);
    }
  }, [sedi, scopoIntestazione]);

  /* ---- Salvataggio profilo azienda ---- */

  const salvaAzienda = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const body: Record<string, string | number | null> = {};
      for (const [k, v] of Object.entries(editData)) {
        if (k === 'id' || k === 'campi_mancanti') continue;
        const original = data ? (data as unknown as Record<string, unknown>)[k] : undefined;
        const origStr = original !== null && original !== undefined ? String(original) : '';
        if (v !== origStr) {
          body[k] = v || null;
        }
      }
      if (Object.keys(body).length === 0) {
        setSuccess('Nessuna modifica');
        return;
      }
      const res = await fetch('/api/v1/admin/azienda', {
        method: 'PATCH',
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      setSuccess('Profilo aziendale aggiornato');
      await carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setSaving(false);
    }
  };

  /* ---- Salvataggio sede operativa ---- */

  const salvaSede = async (sedeId: string) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const sedeOrig = sedi.find((s) => s.id === sedeId);
      const body: Record<string, string | null> = {};
      const se = sedeEdit[sedeId] || {};
      for (const k of ['indirizzo', 'civico', 'cap', 'citta', 'provincia', 'nazione', 'telefono', 'email', 'pec', 'iban', 'banca', 'intestatario_conto', 'regime_fiscale']) {
        const origVal = sedeOrig ? String((sedeOrig as unknown as Record<string, unknown>)[k] ?? '') : '';
        if ((se[k] ?? '') !== origVal) {
          body[k] = se[k] || null;
        }
      }
      if (Object.keys(body).length === 0) {
        setSuccess('Nessuna modifica');
        return;
      }
      const res = await fetch(`/api/v1/unita/${sedeId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      setSuccess(`Sede aggiornata`);
      await carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setSaving(false);
    }
  };

  /* ---- Creazione ramo aziendale ---- */

  const creaRamo = async () => {
    if (!newRamoNome.trim()) return;
    setSavingRamo(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch('/api/v1/unita', {
        method: 'POST',
        headers,
        body: JSON.stringify({ nome: newRamoNome.trim(), tipo: newRamoTipo }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      setSuccess(`Ramo "${newRamoNome.trim()}" creato. Vai su Sistema > Moduli per attivare i moduli.`);
      setNewRamoNome('');
      setShowNewRamo(false);
      await carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore');
    } finally {
      setSavingRamo(false);
    }
  };

  const eliminaRamo = async (ramoId: string) => {
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/v1/unita/${ramoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      setSuccess('Ramo eliminato');
      await carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore');
    }
  };

  const rinominaRamo = async (ramoId: string) => {
    if (!renameValue.trim()) return;
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/v1/unita/${ramoId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ nome: renameValue.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      setSuccess(`Ramo rinominato in "${renameValue.trim()}"`);
      setRenamingId(null);
      setRenameValue('');
      await carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore');
    }
  };

  const chiudiRamo = async () => {
    if (!closingRamo) return;
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/v1/unita/${closingRamo.id}/chiudi`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ conferma_nome: closeConfirmName }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      setSuccess(`Ramo "${closingRamo.nome}" chiuso definitivamente.`);
      setClosingRamo(null);
      setCloseConfirmName('');
      await carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore');
    }
  };

  /* ---- Helpers ---- */

  const v = (campo: string) => editData[campo] ?? '';
  const set = (campo: string, valore: string) => setEditData((prev) => ({ ...prev, [campo]: valore }));
  const sv = (sedeId: string, campo: string) => sedeEdit[sedeId]?.[campo] ?? '';
  const sset = (sedeId: string, campo: string, valore: string) =>
    setSedeEdit((prev) => ({ ...prev, [sedeId]: { ...(prev[sedeId] || {}), [campo]: valore } }));

  const isMancante = (campo: string) => data?.campi_mancanti?.includes(campo) ?? false;

  /* Full-page "Caricamento..." solo al primo caricamento: durante i refetch
     post-salvataggio (carica()) i Tabs restano montati, cosi' la scheda
     attiva non viene mai persa. */
  if (loading && !data) return <div className="p-6">Caricamento...</div>;

  const tipoSoggetto = v('tipo_soggetto');
  const mostraREA = ['societa_capitali', 'cooperativa'].includes(tipoSoggetto);
  const mostraAlbo = tipoSoggetto === 'professionista';
  const mostraDenominazione = !['ditta_individuale', 'professionista'].includes(tipoSoggetto);
  const mostraNomeCognome = ['ditta_individuale', 'professionista'].includes(tipoSoggetto);

  return (
    <div className="mx-auto w-full space-y-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--color-foreground)' }}>
          Impostazioni Azienda
        </h1>
        {data?.campi_mancanti && data.campi_mancanti.length > 0 && (
          <Badge variant="destructive">
            {data.campi_mancanti.length} campi obbligatori mancanti
          </Badge>
        )}
      </div>

      {error && (
        <div className="rounded-md border p-3" style={{ borderColor: 'var(--color-destructive)', color: 'var(--color-destructive)' }}>
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-md border p-3" style={{ borderColor: 'var(--color-success, #22c55e)', color: 'var(--color-success, #22c55e)' }}>
          {success}
        </div>
      )}

      <Tabs value={tabAttivo} onValueChange={cambiaTab}>
        <TabsList>
          <TabsTrigger value="documenti">Intestazione Documenti</TabsTrigger>
          <TabsTrigger value="anagrafica">Dati Aziendali</TabsTrigger>
          <TabsTrigger value="sede">Sede Legale</TabsTrigger>
          <TabsTrigger value="fiscale">REA e Fiscale</TabsTrigger>
          <TabsTrigger value="contatti">Contatti</TabsTrigger>
          <TabsTrigger value="pagamenti">Pagamenti</TabsTrigger>
          <TabsTrigger value="unita-misura">Unita di misura</TabsTrigger>
          <TabsTrigger value="modelli-email">Modelli email</TabsTrigger>
          <TabsTrigger value="sedi">Sedi Operative</TabsTrigger>
          <TabsTrigger value="multitenant">Configurazione Multi-Tenant</TabsTrigger>
        </TabsList>

        {/* Tab unificato: scope selector + form contestuale per tenant o ramo.
            Ogni ramo ha intestazione INDIPENDENTE (logo, indirizzo, contatti, IBAN).
            Tenant e ramo sono salvati su endpoint diversi. */}
        <TabsContent value="documenti">
          {(() => {
            const sedeAttiva = scopoIntestazione !== 'tenant'
              ? sedi.find((s) => s.id === scopoIntestazione)
              : null;

            return (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Intestazione Documenti</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
                      Ogni ramo ha la sua intestazione (logo, eventualmente indirizzo, contatti, IBAN) che appare
                      sui documenti emessi da quel ramo. P.IVA, C.F., REA, ragione sociale e indirizzo sede legale
                      sono dati fiscali UNICI per l'azienda, gestiti nei tab "Dati Aziendali" / "Sede Legale" / "REA".
                    </p>

                    <div className="space-y-1">
                      <Label className="text-sm font-medium">Modifica intestazione per:</Label>
                      <Select value={scopoIntestazione} onValueChange={setScopoIntestazione}>
                        <SelectTrigger><SelectValue placeholder="Seleziona un ramo..." /></SelectTrigger>
                        <SelectContent>
                          {sedi.filter((s) => s.stato !== 'chiuso').map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.nome}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {sedi.length === 0 && (
                        <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                          Nessun ramo configurato. Per aggiungere rami vai al tab "Configurazione Multi-Tenant".
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Form TENANT (anagrafica fiscale + intestazione azienda principale) */}
                {scopoIntestazione === 'tenant' && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Intestazione: Azienda principale</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">

                      <div className="space-y-2">
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                          Logo aziendale
                        </h3>
                        <LogoUploader
                          token={token}
                          uploadUrl="/api/v1/admin/azienda/logo"
                          logoPath={data?.logo_path ?? null}
                          hint="PNG, JPG o SVG, max 2MB. Usato per i documenti emessi senza ramo specifico."
                          onChange={(newPath) => {
                            setData((d) => (d ? { ...d, logo_path: newPath } : d));
                            setSuccess(newPath ? 'Logo aziendale caricato' : 'Logo aziendale rimosso');
                          }}
                        />
                      </div>

                      <Separator />

                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                          Identita azienda (condivisa tra tutti i rami)
                        </h3>
                        {mostraDenominazione && (
                          <Campo label="Denominazione / Ragione sociale" value={v('denominazione')} onChange={(val) => set('denominazione', val)} placeholder="es. Floraltech S.R.L." maxLength={80} required={isMancante('denominazione')} />
                        )}
                        {mostraNomeCognome && (
                          <div className="grid grid-cols-3 gap-4">
                            <Campo label="Titolo" value={v('titolo')} onChange={(val) => set('titolo', val)} maxLength={10} />
                            <Campo label="Nome" value={v('nome_legale')} onChange={(val) => set('nome_legale', val)} maxLength={60} />
                            <Campo label="Cognome" value={v('cognome_legale')} onChange={(val) => set('cognome_legale', val)} maxLength={60} />
                          </div>
                        )}
                        <div className="grid grid-cols-3 gap-4">
                          <Campo label="Partita IVA" value={v('partita_iva')} onChange={(val) => set('partita_iva', val)} maxLength={11} required={isMancante('partita_iva')} />
                          <Campo label="Codice Fiscale" value={v('codice_fiscale')} onChange={(val) => set('codice_fiscale', val)} maxLength={16} required={isMancante('codice_fiscale')} />
                          <Campo label="Forma giuridica" value={v('forma_giuridica')} onChange={(val) => set('forma_giuridica', val)} maxLength={100} />
                        </div>
                      </div>

                      <Separator />

                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                          Indirizzo sede legale
                        </h3>
                        <div className="grid grid-cols-4 gap-4">
                          <div className="col-span-3">
                            <Campo label="Indirizzo" value={v('sede_indirizzo')} onChange={(val) => set('sede_indirizzo', val)} maxLength={60} required={isMancante('sede_indirizzo')} />
                          </div>
                          <Campo label="Civico" value={v('sede_civico')} onChange={(val) => set('sede_civico', val)} maxLength={8} />
                        </div>
                        <div className="grid grid-cols-4 gap-4">
                          <Campo label="CAP" value={v('sede_cap')} onChange={(val) => set('sede_cap', val)} maxLength={5} required={isMancante('sede_cap')} />
                          <div className="col-span-2">
                            <Campo label="Comune" value={v('sede_comune')} onChange={(val) => set('sede_comune', val)} maxLength={60} required={isMancante('sede_comune')} />
                          </div>
                          <Campo label="Prov." value={v('sede_provincia')} onChange={(val) => set('sede_provincia', val)} maxLength={2} />
                        </div>
                      </div>

                      <Separator />

                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                          Contatti
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                          <Campo label="Telefono" value={v('telefono')} onChange={(val) => set('telefono', val)} maxLength={50} />
                          <Campo label="Fax" value={v('fax')} onChange={(val) => set('fax', val)} maxLength={50} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <Campo label="Email" value={v('email')} onChange={(val) => set('email', val)} maxLength={256} />
                          <Campo label="PEC" value={v('pec')} onChange={(val) => set('pec', val)} maxLength={256} />
                        </div>
                      </div>

                      {mostraREA && (
                        <>
                          <Separator />
                          <div className="space-y-3">
                            <h3 className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                              Iscrizione REA (obbligatorio per SRL/SpA)
                            </h3>
                            <div className="grid grid-cols-3 gap-4">
                              <Campo label="Ufficio CCIAA" value={v('rea_ufficio')} onChange={(val) => set('rea_ufficio', val)} maxLength={2} required={isMancante('rea_ufficio')} />
                              <Campo label="Numero REA" value={v('rea_numero')} onChange={(val) => set('rea_numero', val)} maxLength={20} required={isMancante('rea_numero')} />
                              <Campo label="Capitale sociale" value={v('rea_capitale_sociale')} onChange={(val) => set('rea_capitale_sociale', val)} placeholder="10000.00" />
                            </div>
                          </div>
                        </>
                      )}

                      <div className="flex justify-end pt-2">
                        <Button onClick={salvaAzienda} disabled={saving}>
                          {saving ? 'Salvataggio...' : 'Salva intestazione azienda'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Form RAMO (intestazione del ramo selezionato) */}
                {sedeAttiva && (
                  <Card>
                    <CardHeader>
                      <CardTitle>
                        Intestazione: {sedeAttiva.nome}{' '}
                        <Badge variant="outline">{sedeAttiva.tipo}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                        Questi dati sostituiscono quelli aziendali sui documenti emessi
                        da questo ramo. L'anagrafica fiscale (P.IVA, C.F., REA) resta quella aziendale.
                      </p>

                      <div className="space-y-2">
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                          Logo del ramo
                        </h3>
                        <LogoUploader
                          token={token}
                          uploadUrl={`/api/v1/unita/${sedeAttiva.id}/logo`}
                          deleteUrl={`/api/v1/unita/${sedeAttiva.id}/logo`}
                          logoPath={sedeAttiva.logo_path}
                          hint="Logo specifico del ramo. Sostituisce quello aziendale sui documenti emessi da questo ramo."
                          onChange={(newPath) => {
                            setSedi((arr) => arr.map((s) =>
                              s.id === sedeAttiva.id ? { ...s, logo_path: newPath } : s
                            ));
                            setSuccess(newPath ? `Logo ramo ${sedeAttiva.nome} caricato` : `Logo ramo ${sedeAttiva.nome} rimosso`);
                          }}
                        />
                      </div>

                      <Separator />

                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                          Indirizzo operativo del ramo
                        </h3>
                        <div className="grid grid-cols-4 gap-4">
                          <div className="col-span-3">
                            <Campo label="Indirizzo" value={sv(sedeAttiva.id, 'indirizzo')} onChange={(val) => sset(sedeAttiva.id, 'indirizzo', val)} maxLength={60} />
                          </div>
                          <Campo label="Civico" value={sv(sedeAttiva.id, 'civico')} onChange={(val) => sset(sedeAttiva.id, 'civico', val)} maxLength={8} />
                        </div>
                        <div className="grid grid-cols-4 gap-4">
                          <Campo label="CAP" value={sv(sedeAttiva.id, 'cap')} onChange={(val) => sset(sedeAttiva.id, 'cap', val)} maxLength={5} />
                          <div className="col-span-2">
                            <Campo label="Citta" value={sv(sedeAttiva.id, 'citta')} onChange={(val) => sset(sedeAttiva.id, 'citta', val)} maxLength={60} />
                          </div>
                          <Campo label="Prov." value={sv(sedeAttiva.id, 'provincia')} onChange={(val) => sset(sedeAttiva.id, 'provincia', val)} maxLength={2} />
                        </div>
                      </div>

                      <Separator />

                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                          Contatti del ramo
                        </h3>
                        <div className="grid grid-cols-3 gap-4">
                          <Campo label="Telefono" value={sv(sedeAttiva.id, 'telefono')} onChange={(val) => sset(sedeAttiva.id, 'telefono', val)} />
                          <Campo label="Email" value={sv(sedeAttiva.id, 'email')} onChange={(val) => sset(sedeAttiva.id, 'email', val)} />
                          <Campo label="PEC" value={sv(sedeAttiva.id, 'pec')} onChange={(val) => sset(sedeAttiva.id, 'pec', val)} />
                        </div>
                      </div>

                      <Separator />

                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                          Conto corrente dedicato al ramo
                        </h3>
                        <div className="grid grid-cols-3 gap-4">
                          <Campo label="IBAN" value={sv(sedeAttiva.id, 'iban')} onChange={(val) => sset(sedeAttiva.id, 'iban', val)} maxLength={34} />
                          <Campo label="Banca" value={sv(sedeAttiva.id, 'banca')} onChange={(val) => sset(sedeAttiva.id, 'banca', val)} maxLength={100} />
                          <Campo label="Intestatario" value={sv(sedeAttiva.id, 'intestatario_conto')} onChange={(val) => sset(sedeAttiva.id, 'intestatario_conto', val)} maxLength={100} />
                        </div>
                      </div>

                      <Separator />

                      <div className="space-y-1">
                        <Label className="text-sm font-medium">Regime fiscale (override per ramo)</Label>
                        <Select value={sv(sedeAttiva.id, 'regime_fiscale')} onValueChange={(val) => sset(sedeAttiva.id, 'regime_fiscale', val)}>
                          <SelectTrigger><SelectValue placeholder="Usa quello aziendale (default)" /></SelectTrigger>
                          <SelectContent>
                            {REGIMI_FISCALI.map((r) => (
                              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                          Lascia vuoto per usare il regime fiscale aziendale.
                        </p>
                      </div>

                      <div className="flex justify-end pt-2">
                        <Button onClick={() => salvaSede(sedeAttiva.id)} disabled={saving}>
                          {saving ? 'Salvataggio...' : `Salva intestazione ramo ${sedeAttiva.nome}`}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })()}
        </TabsContent>

        {/* ── Tab Dati Aziendali ── */}
        <TabsContent value="anagrafica">
          <Card>
            <CardHeader>
              <CardTitle>Dati Anagrafici</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-sm font-medium">Tipo soggetto giuridico</Label>
                  <Select value={v('tipo_soggetto')} onValueChange={(val) => set('tipo_soggetto', val)}>
                    <SelectTrigger><SelectValue placeholder="Seleziona tipo..." /></SelectTrigger>
                    <SelectContent>
                      {TIPI_SOGGETTO.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Campo label="Forma giuridica" value={v('forma_giuridica')} onChange={(val) => set('forma_giuridica', val)} placeholder="es. S.R.L." maxLength={100} />
              </div>

              <Separator />

              {mostraDenominazione && (
                <Campo label="Denominazione / Ragione sociale" value={v('denominazione')} onChange={(val) => set('denominazione', val)} placeholder="es. Floraltech S.R.L." maxLength={80} required={isMancante('denominazione')} />
              )}

              {mostraNomeCognome && (
                <div className="grid grid-cols-3 gap-4">
                  <Campo label="Titolo" value={v('titolo')} onChange={(val) => set('titolo', val)} placeholder="es. Dott." maxLength={10} />
                  <Campo label="Nome" value={v('nome_legale')} onChange={(val) => set('nome_legale', val)} maxLength={60} required={isMancante('nome_legale')} />
                  <Campo label="Cognome" value={v('cognome_legale')} onChange={(val) => set('cognome_legale', val)} maxLength={60} required={isMancante('cognome_legale')} />
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <Campo label="Partita IVA" value={v('partita_iva')} onChange={(val) => set('partita_iva', val)} maxLength={11} required={isMancante('partita_iva')} />
                <Campo label="Codice Fiscale" value={v('codice_fiscale')} onChange={(val) => set('codice_fiscale', val)} maxLength={16} required={isMancante('codice_fiscale')} />
                <Campo label="Paese (ISO)" value={v('id_paese')} onChange={(val) => set('id_paese', val)} placeholder="IT" maxLength={2} />
              </div>

              <Campo label="Cod. EORI" value={v('cod_eori')} onChange={(val) => set('cod_eori', val)} placeholder="Operatori economici registrati" maxLength={17} />

              <Separator />

              <LogoUploader
                token={token}
                uploadUrl="/api/v1/admin/azienda/logo"
                logoPath={data?.logo_path ?? null}
                hint="PNG, JPG o SVG. Max 2MB. Apparirà nell'intestazione di preventivi, ordini e fatture. Se un ramo ha un suo logo, quello del ramo prevale."
                onChange={(newPath) => {
                  setData((d) => (d ? { ...d, logo_path: newPath } : d));
                  setSuccess(newPath ? 'Logo caricato' : 'Logo rimosso');
                }}
              />

              <div className="flex justify-end pt-2">
                <Button onClick={salvaAzienda} disabled={saving}>
                  {saving ? 'Salvataggio...' : 'Salva'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab Sede Legale ── */}
        <TabsContent value="sede">
          <Card>
            <CardHeader>
              <CardTitle>Sede Legale</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <div className="col-span-3">
                  <Campo label="Indirizzo" value={v('sede_indirizzo')} onChange={(val) => set('sede_indirizzo', val)} maxLength={60} required={isMancante('sede_indirizzo')} />
                </div>
                <Campo label="N. Civico" value={v('sede_civico')} onChange={(val) => set('sede_civico', val)} maxLength={8} />
              </div>
              <div className="grid grid-cols-4 gap-4">
                <Campo label="CAP" value={v('sede_cap')} onChange={(val) => set('sede_cap', val)} maxLength={5} required={isMancante('sede_cap')} />
                <div className="col-span-2">
                  <Campo label="Comune" value={v('sede_comune')} onChange={(val) => set('sede_comune', val)} maxLength={60} required={isMancante('sede_comune')} />
                </div>
                <Campo label="Provincia" value={v('sede_provincia')} onChange={(val) => set('sede_provincia', val)} placeholder="CS" maxLength={2} />
              </div>
              <Campo label="Nazione" value={v('sede_nazione')} onChange={(val) => set('sede_nazione', val)} placeholder="IT" maxLength={2} />

              <Separator />
              <h3 className="text-sm font-semibold" style={{ color: 'var(--color-muted-foreground)' }}>
                Stabile Organizzazione (solo soggetti non residenti)
              </h3>
              <div className="grid grid-cols-4 gap-4">
                <div className="col-span-3">
                  <Campo label="Indirizzo" value={v('stabile_org_indirizzo')} onChange={(val) => set('stabile_org_indirizzo', val)} maxLength={60} />
                </div>
                <Campo label="N. Civico" value={v('stabile_org_civico')} onChange={(val) => set('stabile_org_civico', val)} maxLength={8} />
              </div>
              <div className="grid grid-cols-4 gap-4">
                <Campo label="CAP" value={v('stabile_org_cap')} onChange={(val) => set('stabile_org_cap', val)} maxLength={5} />
                <div className="col-span-2">
                  <Campo label="Comune" value={v('stabile_org_comune')} onChange={(val) => set('stabile_org_comune', val)} maxLength={60} />
                </div>
                <Campo label="Provincia" value={v('stabile_org_provincia')} onChange={(val) => set('stabile_org_provincia', val)} maxLength={2} />
              </div>
              <Campo label="Nazione" value={v('stabile_org_nazione')} onChange={(val) => set('stabile_org_nazione', val)} maxLength={2} />

              <div className="flex justify-end pt-2">
                <Button onClick={salvaAzienda} disabled={saving}>
                  {saving ? 'Salvataggio...' : 'Salva'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab REA e Fiscale ── */}
        <TabsContent value="fiscale">
          <Card>
            <CardHeader>
              <CardTitle>Regime Fiscale e Iscrizione REA</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <Label className="text-sm font-medium">
                  Regime fiscale
                  {isMancante('regime_fiscale') && <span style={{ color: 'var(--color-destructive)' }}> *</span>}
                </Label>
                <Select value={v('regime_fiscale')} onValueChange={(val) => set('regime_fiscale', val)}>
                  <SelectTrigger><SelectValue placeholder="Seleziona regime..." /></SelectTrigger>
                  <SelectContent>
                    {REGIMI_FISCALI.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Campo label="Codice SDI" value={v('codice_sdi')} onChange={(val) => set('codice_sdi', val)} placeholder="7 caratteri" maxLength={7} />
              <Campo label="Riferimento Amministrazione" value={v('riferimento_amministrazione')} onChange={(val) => set('riferimento_amministrazione', val)} maxLength={20} />

              {mostraREA && (
                <>
                  <Separator />
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--color-muted-foreground)' }}>
                    Iscrizione REA (obbligatorio per società — art. 2250 c.c.)
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Campo label="Ufficio (provincia CCIAA)" value={v('rea_ufficio')} onChange={(val) => set('rea_ufficio', val)} placeholder="CS" maxLength={2} required={isMancante('rea_ufficio')} />
                    <Campo label="Numero REA" value={v('rea_numero')} onChange={(val) => set('rea_numero', val)} maxLength={20} required={isMancante('rea_numero')} />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <Campo label="Capitale sociale" value={v('rea_capitale_sociale')} onChange={(val) => set('rea_capitale_sociale', val)} placeholder="1000.00" />
                    <div className="space-y-1">
                      <Label className="text-sm font-medium">Socio unico</Label>
                      <Select value={v('rea_socio_unico')} onValueChange={(val) => set('rea_socio_unico', val)}>
                        <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SU">SU — Socio unico</SelectItem>
                          <SelectItem value="SM">SM — Più soci</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm font-medium">
                        Stato liquidazione
                        {isMancante('rea_stato_liquidazione') && <span style={{ color: 'var(--color-destructive)' }}> *</span>}
                      </Label>
                      <Select value={v('rea_stato_liquidazione')} onValueChange={(val) => set('rea_stato_liquidazione', val)}>
                        <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="LN">LN — Non in liquidazione</SelectItem>
                          <SelectItem value="LS">LS — In liquidazione</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}

              {mostraAlbo && (
                <>
                  <Separator />
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--color-muted-foreground)' }}>
                    Albo Professionale
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Campo label="Albo" value={v('albo_professionale')} onChange={(val) => set('albo_professionale', val)} maxLength={60} />
                    <Campo label="Provincia albo" value={v('provincia_albo')} onChange={(val) => set('provincia_albo', val)} maxLength={2} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Campo label="Numero iscrizione" value={v('numero_iscrizione_albo')} onChange={(val) => set('numero_iscrizione_albo', val)} maxLength={60} />
                    <Campo label="Data iscrizione" value={v('data_iscrizione_albo')} onChange={(val) => set('data_iscrizione_albo', val)} placeholder="AAAA-MM-GG" />
                  </div>
                </>
              )}

              <div className="flex justify-end pt-2">
                <Button onClick={salvaAzienda} disabled={saving}>
                  {saving ? 'Salvataggio...' : 'Salva'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab Contatti ── */}
        <TabsContent value="contatti">
          <Card>
            <CardHeader>
              <CardTitle>Contatti Aziendali</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Campo label="Telefono" value={v('telefono')} onChange={(val) => set('telefono', val)} maxLength={50} />
                <Campo label="Fax" value={v('fax')} onChange={(val) => set('fax', val)} maxLength={50} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Campo label="Email" value={v('email')} onChange={(val) => set('email', val)} maxLength={256} />
                <Campo label="PEC" value={v('pec')} onChange={(val) => set('pec', val)} maxLength={256} />
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={salvaAzienda} disabled={saving}>
                  {saving ? 'Salvataggio...' : 'Salva'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Pagamenti */}
        <TabsContent value="pagamenti">
          <ModalitaPagamentoEditor />
        </TabsContent>

        {/* Tab Unita di misura */}
        <TabsContent value="unita-misura">
          <UnitaMisuraEditor />
        </TabsContent>

        {/* Tab Modelli email */}
        <TabsContent value="modelli-email">
          <ModelliEmailEditor />
        </TabsContent>

        {/* Tab Sedi Operative */}
        <TabsContent value="sedi">
          <div className="space-y-4">
            {sedi.length === 0 && (
              <Card>
                <CardContent className="py-8 text-center" style={{ color: 'var(--color-muted-foreground)' }}>
                  Nessuna sede operativa configurata. Le sedi si creano dalla sezione Unità Organizzative.
                </CardContent>
              </Card>
            )}
            {sedi.map((sede) => (
              <Card key={sede.id}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {sede.nome}
                    <Badge variant="outline">{sede.tipo}</Badge>
                    {sede.codice && <Badge variant="secondary">{sede.codice}</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-4 gap-4">
                    <div className="col-span-3">
                      <Campo label="Indirizzo" value={sv(sede.id, 'indirizzo')} onChange={(val) => sset(sede.id, 'indirizzo', val)} maxLength={60} />
                    </div>
                    <Campo label="N. Civico" value={sv(sede.id, 'civico')} onChange={(val) => sset(sede.id, 'civico', val)} maxLength={8} />
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <Campo label="CAP" value={sv(sede.id, 'cap')} onChange={(val) => sset(sede.id, 'cap', val)} maxLength={5} />
                    <div className="col-span-2">
                      <Campo label="Città" value={sv(sede.id, 'citta')} onChange={(val) => sset(sede.id, 'citta', val)} maxLength={60} />
                    </div>
                    <Campo label="Prov." value={sv(sede.id, 'provincia')} onChange={(val) => sset(sede.id, 'provincia', val)} maxLength={2} />
                  </div>

                  <Separator />
                  <div className="grid grid-cols-3 gap-4">
                    <Campo label="Telefono" value={sv(sede.id, 'telefono')} onChange={(val) => sset(sede.id, 'telefono', val)} />
                    <Campo label="Email" value={sv(sede.id, 'email')} onChange={(val) => sset(sede.id, 'email', val)} />
                    <Campo label="PEC" value={sv(sede.id, 'pec')} onChange={(val) => sset(sede.id, 'pec', val)} />
                  </div>

                  <Separator />
                  <h4 className="text-sm font-semibold" style={{ color: 'var(--color-muted-foreground)' }}>Conto corrente dedicato</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <Campo label="IBAN" value={sv(sede.id, 'iban')} onChange={(val) => sset(sede.id, 'iban', val)} maxLength={34} />
                    <Campo label="Banca" value={sv(sede.id, 'banca')} onChange={(val) => sset(sede.id, 'banca', val)} maxLength={100} />
                    <Campo label="Intestatario" value={sv(sede.id, 'intestatario_conto')} onChange={(val) => sset(sede.id, 'intestatario_conto', val)} maxLength={100} />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-sm font-medium">Regime fiscale (override)</Label>
                    <Select value={sv(sede.id, 'regime_fiscale')} onValueChange={(val) => sset(sede.id, 'regime_fiscale', val)}>
                      <SelectTrigger><SelectValue placeholder="Usa quello aziendale" /></SelectTrigger>
                      <SelectContent>
                        {REGIMI_FISCALI.map((r) => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />

                  <LogoUploader
                    token={token}
                    uploadUrl={`/api/v1/unita/${sede.id}/logo`}
                    deleteUrl={`/api/v1/unita/${sede.id}/logo`}
                    logoPath={sede.logo_path}
                    hint="Logo specifico del ramo. Prevale sul logo aziendale per i documenti emessi da questo ramo."
                    onChange={(newPath) => {
                      setSedi((arr) => arr.map((s) =>
                        s.id === sede.id ? { ...s, logo_path: newPath } : s
                      ));
                      setSuccess(newPath ? `Logo ramo ${sede.nome} aggiornato` : `Logo ramo ${sede.nome} rimosso`);
                    }}
                  />

                  <div className="flex justify-end pt-2">
                    <Button onClick={() => salvaSede(sede.id)} disabled={saving}>
                      {saving ? 'Salvataggio...' : 'Salva sede'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* ── Tab Configurazione Multi-Tenant ── */}
        <TabsContent value="multitenant">
          <div className="space-y-6">

            {/* Stato corrente */}
            <Card>
              <CardHeader>
                <CardTitle>Struttura attuale</CardTitle>
              </CardHeader>
              <CardContent>
                {sedi.length === 0 ? (
                  <p className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
                    Nessun ramo o azienda aggiuntiva configurata. Il sistema opera in modalità singola.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {sedi.map((sede) => {
                      const chiuso = sede.stato === 'chiuso';
                      return (
                        <div
                          key={sede.id}
                          className="flex items-center justify-between rounded-md border px-4 py-3"
                          style={chiuso ? { opacity: 0.6 } : undefined}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {renamingId === sede.id ? (
                              <div className="flex items-center gap-2 flex-1">
                                <Input
                                  value={renameValue}
                                  onChange={(e) => setRenameValue(e.target.value)}
                                  className="max-w-xs"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') rinominaRamo(sede.id);
                                    if (e.key === 'Escape') { setRenamingId(null); setRenameValue(''); }
                                  }}
                                />
                                <Button size="sm" onClick={() => rinominaRamo(sede.id)} disabled={!renameValue.trim()}>
                                  Salva
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => { setRenamingId(null); setRenameValue(''); }}>
                                  Annulla
                                </Button>
                              </div>
                            ) : (
                              <div>
                                <span className="font-medium">{sede.nome}</span>
                                <Badge variant="outline" className="ml-2">{sede.tipo}</Badge>
                                {sede.codice && <Badge variant="secondary" className="ml-1">{sede.codice}</Badge>}
                                {chiuso && (
                                  <Badge variant="destructive" className="ml-2">
                                    Chiuso {sede.data_chiusura ? `— ${new Date(sede.data_chiusura).toLocaleDateString('it-IT')}` : ''}
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                          {!chiuso && renamingId !== sede.id && (
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setRenamingId(sede.id); setRenameValue(sede.nome); }}
                              >
                                Rinomina
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setClosingRamo(sede); setCloseConfirmName(''); }}
                                style={{ color: 'var(--color-destructive)' }}
                              >
                                Chiudi
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Modale conferma chiusura ramo */}
            {closingRamo && (
              <Card style={{ borderColor: 'var(--color-destructive)' }}>
                <CardHeader>
                  <CardTitle style={{ color: 'var(--color-destructive)' }}>
                    Chiusura ramo &quot;{closingRamo.nome}&quot;
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-md p-4 space-y-2" style={{ backgroundColor: 'var(--color-destructive)', color: 'white' }}>
                    <p className="font-semibold">Attenzione: questa operazione è IRREVERSIBILE</p>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      <li>Tutti i dati del ramo resteranno visibili in sola lettura</li>
                      <li>Non sarà più possibile creare documenti, scontrini, ordini o movimenti per questo ramo</li>
                      <li>Gli utenti non potranno più selezionare questo ramo</li>
                      <li>Il ramo non potrà essere riattivato — se necessario, dovrai crearne uno nuovo</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Per confermare, digita esattamente il nome del ramo: <strong>{closingRamo.nome}</strong>
                    </Label>
                    <Input
                      value={closeConfirmName}
                      onChange={(e) => setCloseConfirmName(e.target.value)}
                      placeholder={closingRamo.nome}
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      onClick={chiudiRamo}
                      disabled={closeConfirmName !== closingRamo.nome}
                    >
                      Chiudi definitivamente
                    </Button>
                    <Button variant="ghost" onClick={() => { setClosingRamo(null); setCloseConfirmName(''); }}>
                      Annulla
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Azioni */}
            <div className="grid grid-cols-2 gap-4">
              {/* Aggiungi Azienda (multi-tenant) */}
              <Card>
                <CardHeader>
                  <CardTitle>Aggiungi Azienda</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="mb-4 text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
                    Crea un tenant separato con contabilità, utenti e dati completamente indipendenti.
                  </p>
                  <Button variant="outline" disabled>
                    Disponibile nella versione SaaS
                  </Button>
                </CardContent>
              </Card>

              {/* Aggiungi Ramo Aziendale */}
              <Card>
                <CardHeader>
                  <CardTitle>Aggiungi Ramo Aziendale</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="mb-4 text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
                    Crea un ramo operativo con moduli, utenti e dati separati ma contabilità condivisa.
                  </p>
                  {!showNewRamo ? (
                    <Button onClick={() => setShowNewRamo(true)}>
                      Aggiungi ramo
                    </Button>
                  ) : (
                    <div className="space-y-3 rounded-md border p-4">
                      <div>
                        <Label className="mb-1 block text-sm font-medium">Nome ramo *</Label>
                        <Input
                          value={newRamoNome}
                          onChange={(e) => setNewRamoNome(e.target.value)}
                          placeholder="es. Sede Milano, Divisione Commerciale..."
                        />
                      </div>
                      <div>
                        <Label className="mb-1 block text-sm font-medium">Tipo</Label>
                        <Select value={newRamoTipo} onValueChange={setNewRamoTipo}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sede_principale">Sede principale</SelectItem>
                            <SelectItem value="filiale">Filiale</SelectItem>
                            <SelectItem value="divisione">Divisione</SelectItem>
                            <SelectItem value="reparto">Reparto</SelectItem>
                            <SelectItem value="magazzino">Magazzino</SelectItem>
                            <SelectItem value="negozio">Negozio</SelectItem>
                            <SelectItem value="ufficio">Ufficio</SelectItem>
                            <SelectItem value="altro">Altro</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={creaRamo} disabled={savingRamo || !newRamoNome.trim()}>
                          {savingRamo ? 'Creazione...' : 'Crea ramo'}
                        </Button>
                        <Button variant="ghost" onClick={() => { setShowNewRamo(false); setNewRamoNome(''); }}>
                          Annulla
                        </Button>
                      </div>
                      <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                        Dopo la creazione, vai su Sistema &gt; Moduli per attivare i moduli per questo ramo,
                        poi su Sistema &gt; Utenti per assegnare gli utenti.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Guida */}
            {sedi.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Prossimi passi</CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="list-inside list-decimal space-y-2 text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
                    <li><strong>Attiva i moduli</strong> — vai su Sistema &gt; Moduli e attiva i moduli per ogni ramo</li>
                    <li><strong>Assegna gli utenti</strong> — vai su Sistema &gt; Utenti, apri un utente e nella sezione "Rami operativi" assegnalo ai rami con il ruolo appropriato</li>
                    <li><strong>Seleziona il ramo</strong> — dopo l'assegnazione, nella barra superiore apparirà il selettore per cambiare ramo</li>
                  </ol>
                </CardContent>
              </Card>
            )}

          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ImpostazioniAzienda;
